import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  UserPlus, X as XIcon, Pencil, ShieldCheck, Ban, CheckCircle2, Eye, EyeOff,
} from "lucide-react";

import {
  fetchUsers, createUser, patchUser,
  type UserRow, RIGHT_LABEL,
} from "../api/users";
import { explainError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { shortDate, fmtIST } from "../lib/format";

/* ──────────────────────────────────────────────────────────────────────
   /users — staff accounts. Modeled on the simpleey project's TeamSettings:
   list + add + rights select + enable/disable + password reset.
   The whole surface is SuperAdmin-only (backend 403s everyone else; the
   nav link is rights-gated too).
   ────────────────────────────────────────────────────────────────────── */

export function Users() {
  const qc = useQueryClient();
  const { me } = useAuth();
  const isSuper = me?.group_name === "SuperAdmin";

  const q = useQuery({ queryKey: ["users"], queryFn: fetchUsers });
  const [showAdd, setShowAdd] = useState(false);
  const [editFor, setEditFor] = useState<UserRow | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ["users"] });

  return (
    <div className="p-6 max-w-[1080px] mx-auto">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">
            Administration · Users
          </div>
          <h1 className="font-display text-[28px] leading-tight">Staff accounts</h1>
          <p className="font-sans text-[13px] text-[var(--fg-muted)] mt-1 max-w-[640px]">
            One live session per account — a new login signs out the previous one.
            Every login records its IP. Rights: <strong>Super admin</strong> (everything,
            incl. this page), <strong>Admin</strong> (operations), <strong>User</strong> (read-only browse).
          </p>
        </div>
        {isSuper && (
          <button onClick={() => setShowAdd(true)} className="np-btn text-[12px] whitespace-nowrap">
            <UserPlus size={12} className="inline mr-1.5" />Add user
          </button>
        )}
      </header>

      {q.isLoading && <div className="font-mono text-[11px] text-[var(--fg-faint)]">loading…</div>}
      {q.isError && (
        <div className="np-banner">
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
          {explainError(q.error)}
        </div>
      )}

      {q.data && (
        <div className="np-card overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr style={{ borderBottom: "1px solid var(--rule-strong)" }}>
                <Th>User</Th>
                <Th>Rights</Th>
                <Th>Status</Th>
                <Th>Last login</Th>
                <Th>Session</Th>
                <Th className="text-right pr-4">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {q.data.users.map(u => (
                <Row
                  key={u.user_id}
                  row={u}
                  selfId={me?.user_id ?? -1}
                  canEdit={isSuper}
                  onChanged={refresh}
                  onEdit={() => setEditFor(u)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && q.data && (
        <AddUserDialog
          groups={q.data.assignable_groups}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
        />
      )}
      {editFor && q.data && (
        <EditUserDialog
          row={editFor}
          selfId={me?.user_id ?? -1}
          groups={q.data.assignable_groups}
          onClose={() => setEditFor(null)}
          onSaved={() => { setEditFor(null); refresh(); }}
        />
      )}
    </div>
  );
}

/* ─── Row ────────────────────────────────────────────────────────────── */
function Row({
  row, selfId, canEdit, onChanged, onEdit,
}: {
  row: UserRow;
  selfId: number;
  canEdit: boolean;
  onChanged: () => void;
  onEdit: () => void;
}) {
  const patch = useMutation({
    mutationFn: (p: Parameters<typeof patchUser>[1]) => patchUser(row.user_id, p),
    onSuccess: onChanged,
  });
  const isSelf = row.user_id === selfId;

  return (
    <tr style={{ borderTop: "1px solid var(--rule)", opacity: row.is_active ? 1 : 0.55 }}>
      <Td>
        <div className="font-display text-[14px]">
          {row.username}
          {isSelf && <span className="np-chip np-chip--admin ml-2">you</span>}
        </div>
        <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-0.5">
          #{row.user_id}{row.email ? ` · ${row.email}` : ""}
        </div>
      </Td>
      <Td>
        <span className="font-mono text-[11px]">
          {RIGHT_LABEL[row.group_name] ?? row.group_name ?? "—"}
        </span>
      </Td>
      <Td>
        {row.locked ? (
          <span className="font-mono text-[10px] uppercase tracking-folio" style={{ color: "var(--accent)" }}>
            locked
          </span>
        ) : row.is_active ? (
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--primary)]">
            <CheckCircle2 size={10} className="inline mr-1" />active
          </span>
        ) : (
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">
            disabled
          </span>
        )}
        {row.failed_login_count > 0 && (
          <div className="font-mono text-[9px] text-[var(--fg-faint)] mt-0.5">
            {row.failed_login_count} failed attempts
          </div>
        )}
      </Td>
      <Td>
        {row.last_login_utc ? (
          <>
            <div className="font-mono text-[11px]">
              {shortDate(row.last_login_utc)}
              <span className="text-[var(--fg-muted)]"> · {fmtIST(row.last_login_utc)}</span>
            </div>
            {row.last_login_ip && (
              <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-0.5">
                from {row.last_login_ip}
              </div>
            )}
          </>
        ) : (
          <span className="font-mono text-[10px] text-[var(--fg-faint)]">never</span>
        )}
      </Td>
      <Td>
        {row.session ? (
          <>
            <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--primary)]">live</span>
            <div className="font-mono text-[10px] text-[var(--fg-faint)] mt-0.5">
              {row.session.ip ?? "—"}
            </div>
          </>
        ) : (
          <span className="font-mono text-[10px] text-[var(--fg-faint)]">—</span>
        )}
      </Td>
      <Td className="pr-4">
        {canEdit && (
          <div className="flex flex-wrap justify-end gap-1">
            <button
              onClick={onEdit}
              className="np-btn-ghost text-[11px] whitespace-nowrap"
              title="Edit username, email, password, rights"
            >
              <Pencil size={10} className="inline mr-1" />Edit
            </button>
            <button
              onClick={() => patch.mutate({ is_active: !row.is_active })}
              disabled={patch.isPending || isSelf}
              className="np-btn-ghost text-[11px] whitespace-nowrap disabled:opacity-40"
              title={isSelf ? "You cannot disable your own account"
                : row.is_active ? "Disable the account and kill its session" : "Re-enable the account"}
            >
              {row.is_active
                ? <><Ban size={10} className="inline mr-1" />Disable</>
                : <><ShieldCheck size={10} className="inline mr-1" />Enable</>}
            </button>
          </div>
        )}
        {patch.isError && (
          <div className="font-mono text-[9px] mt-1 text-right" style={{ color: "var(--accent)" }}>
            {explainError(patch.error)}
          </div>
        )}
      </Td>
    </tr>
  );
}

/* ─── Add user ───────────────────────────────────────────────────────── */
function AddUserDialog({
  groups, onClose, onSaved,
}: {
  groups: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [groupId, setGroupId] = useState(4);   // default: User

  const save = useMutation({
    mutationFn: () => createUser({
      username: username.trim(),
      password,
      email: email.trim() || null,
      group_id: groupId,
    }),
    onSuccess: onSaved,
  });

  const valid = username.trim().length >= 3 && password.length >= 6;

  return (
    <Dialog title="Add user" onClose={onClose}>
      <div className="p-5 space-y-4">
        <Field label="Username">
          <input autoFocus value={username} onChange={(e) => setUsername(e.target.value)}
                 placeholder="e.g. operator2"
                 className="w-full px-2 py-1.5 text-[12px] font-mono"
                 style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
        </Field>
        <Field label="Email (optional)">
          <input value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="name@simpleey.io"
                 className="w-full px-2 py-1.5 text-[12px] font-mono"
                 style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
        </Field>
        <Field label="Password" hint="Minimum 6 characters. Editable later from the Edit button.">
          <PasswordInput value={password} onChange={setPassword} placeholder="••••••" autoFocus={false} />
        </Field>
        <Field label="Rights">
          <div className="flex gap-2">
            {Object.entries(groups).map(([id, name]) => {
              const active = groupId === Number(id);
              return (
                <button key={id} type="button" onClick={() => setGroupId(Number(id))}
                        className="px-3 py-1.5 font-mono text-[11px] uppercase tracking-folio"
                        style={{
                          background: active ? "var(--fg)" : "var(--bg-inset)",
                          color: active ? "var(--bg-card)" : "var(--fg-muted)",
                          border: "1px solid var(--rule)",
                        }}>
                  {RIGHT_LABEL[name] ?? name}
                </button>
              );
            })}
          </div>
        </Field>
        {save.isError && (
          <div className="np-banner">
            <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
            {explainError(save.error)}
          </div>
        )}
      </div>
      <DialogFooter
        onClose={onClose}
        onSave={() => save.mutate()}
        saving={save.isPending}
        disabled={!valid}
        saveLabel="Create user"
      />
    </Dialog>
  );
}

/* ─── Edit user (everything: username, email, password, rights) ──────── */
function EditUserDialog({
  row, selfId, groups, onClose, onSaved,
}: {
  row: UserRow;
  selfId: number;
  groups: Record<string, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isSelf = row.user_id === selfId;
  const [username, setUsername] = useState(row.username);
  const [email, setEmail] = useState(row.email ?? "");
  // Pre-filled with the user's CURRENT password (revealed via the eye toggle).
  const [password, setPassword] = useState(row.password_plain ?? "");
  const [groupId, setGroupId] = useState(row.group_id);

  const save = useMutation({
    mutationFn: () => {
      const patch: Parameters<typeof patchUser>[1] = {};
      if (username.trim() !== row.username) patch.username = username.trim();
      if ((email.trim() || "") !== (row.email ?? "")) patch.email = email.trim();
      // Only push a password change if it actually differs from the stored one.
      if (password && password !== (row.password_plain ?? "")) patch.new_password = password;
      if (groupId !== row.group_id) patch.group_id = groupId;
      return patchUser(row.user_id, patch);
    },
    onSuccess: onSaved,
  });

  const groupChanged = groupId !== row.group_id;
  const usernameValid = username.trim().length >= 3;
  const pwdValid = !password || password.length >= 6;
  const dirty = username.trim() !== row.username
    || (email.trim() || "") !== (row.email ?? "")
    || (!!password && password !== (row.password_plain ?? ""))
    || groupChanged;

  return (
    <Dialog title={`Edit · ${row.username}`} onClose={onClose}>
      <div className="p-5 space-y-4">
        <Field label="Username">
          <input autoFocus value={username} onChange={(e) => setUsername(e.target.value)}
                 className="w-full px-2 py-1.5 text-[12px] font-mono"
                 style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
        </Field>
        <Field label="Email (optional)" hint="Leave blank — it's not required.">
          <input value={email} onChange={(e) => setEmail(e.target.value)}
                 placeholder="name@example.com"
                 className="w-full px-2 py-1.5 text-[12px] font-mono"
                 style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }} />
        </Field>
        <Field label="Password" hint="Shows the current password. Changing it signs the user out.">
          <PasswordInput value={password} onChange={setPassword} placeholder="••••••" autoFocus={false} />
        </Field>
        <Field label="Rights">
          <div className="flex gap-2">
            {Object.entries(groups).map(([id, name]) => {
              const active = groupId === Number(id);
              const disabled = isSelf && Number(id) !== 2;   // can't demote yourself
              return (
                <button key={id} type="button"
                        onClick={() => !disabled && setGroupId(Number(id))}
                        disabled={disabled}
                        title={disabled ? "You cannot change your own rights" : ""}
                        className="px-3 py-1.5 font-mono text-[11px] uppercase tracking-folio disabled:opacity-40"
                        style={{
                          background: active ? "var(--fg)" : "var(--bg-inset)",
                          color: active ? "var(--bg-card)" : "var(--fg-muted)",
                          border: "1px solid var(--rule)",
                        }}>
                  {RIGHT_LABEL[name] ?? name}
                </button>
              );
            })}
          </div>
          {groupChanged && (
            <div className="font-mono text-[10px] text-[var(--fg-muted)] mt-1">
              Changing rights signs the user out (the new role takes effect on next login).
            </div>
          )}
        </Field>
        {save.isError && (
          <div className="np-banner">
            <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] mr-2">Error</span>
            {explainError(save.error)}
          </div>
        )}
      </div>
      <DialogFooter
        onClose={onClose}
        onSave={() => save.mutate()}
        saving={save.isPending}
        disabled={!dirty || !usernameValid || !pwdValid}
        saveLabel="Save changes"
      />
    </Dialog>
  );
}

/* ─── Password field with show/hide eye ──────────────────────────────── */
function PasswordInput({
  value, onChange, placeholder, autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        value={value}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full pl-2 pr-9 py-1.5 text-[12px] font-mono"
        style={{ background: "var(--bg-inset)", border: "1px solid var(--rule)" }}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        tabIndex={-1}
        title={show ? "Hide password" : "Show password"}
        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-[var(--fg-faint)] hover:text-[var(--fg)]"
      >
        {show ? <EyeOff size={13} /> : <Eye size={13} />}
      </button>
    </div>
  );
}

/* ─── Shared dialog chrome ───────────────────────────────────────────── */
function Dialog({ title, onClose, children }: {
  title: string; onClose: () => void; children: React.ReactNode;
}) {
  return (
    <div onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: "rgba(0,0,0,0.5)" }}>
      <div onClick={(e) => e.stopPropagation()} className="np-card w-full max-w-[440px]">
        <div className="px-5 py-3 flex items-center justify-between"
             style={{ borderBottom: "1px solid var(--rule)" }}>
          <div className="font-display text-[18px]">{title}</div>
          <button onClick={onClose} className="np-btn-ghost p-1.5"><XIcon size={13} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function DialogFooter({ onClose, onSave, saving, disabled, saveLabel }: {
  onClose: () => void; onSave: () => void;
  saving: boolean; disabled: boolean; saveLabel: string;
}) {
  return (
    <div className="px-5 py-3 flex items-center justify-end gap-2"
         style={{ borderTop: "1px solid var(--rule)" }}>
      <button onClick={onClose} className="np-btn-ghost text-[12px]">Cancel</button>
      <button onClick={onSave} disabled={disabled || saving} className="np-btn text-[12px]">
        {saving ? "saving…" : saveLabel}
      </button>
    </div>
  );
}

function Th({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <th className={`text-left px-3 py-2 font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] ${className}`}>
      {children}
    </th>
  );
}
function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3 py-2 align-top ${className}`}>{children}</td>;
}
function Field({ label, hint, children }: {
  label: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">{label}</div>
      {children}
      {hint && <div className="font-mono text-[10px] text-[var(--fg-muted)] mt-1">{hint}</div>}
    </label>
  );
}
